/**
 * Nosto-SearchController.js
 *
 * Coordina las búsquedas de productos y keywords mediante Nosto.
 * Administra los contextos de página de resultados y autocomplete,
 * conecta filtros, ordenamiento, paginación, tracking y renderizado
 * con los helpers compartidos de Nosto.
 *
 * Este archivo no renderiza directamente product cards ni contiene
 * la estructura visual específica de facets o keywords.
 */

(function initializeNostoSearchController(global) {
    "use strict";

    class NostoSearchController {
        /**
         * Crea una instancia del controller de Search.
         */
        constructor({
            client,
            facetManager,
            pageHitStore,
            modalHitStore,
            pageRenderer,
            modalRenderer,
            input,
            sortElement = null,
            loadMoreElement = null,
            pageSize = 20,
            modalSize = 5,
            keywordSize = 8,
            minimumQueryLength = 2,
            debounceDelay = 300,
            syncSortToUrl = true,
            sortUrlParam = "sort",
            productFields = [
                "name",
                "url",
                "imageUrl",
            ],
            modalProductFields = [
                "name",
                "url",
                "imageUrl",
            ],
            keywordFields = [
                "keyword",
                "_highlight.keyword",
                "_redirect",
            ],
            onFacets = null,
            onKeywords = null,
            onResponse = null,
            onError = null,
            onLoadingChange = null,
            onSearchStart = null,
            debug = false,
        } = {}) {
            this.client = client;
            this.facetManager =
                facetManager;

            this.pageHitStore =
                pageHitStore;

            this.modalHitStore =
                modalHitStore;

            this.pageRenderer =
                pageRenderer;

            this.modalRenderer =
                modalRenderer;

            this.input =
                this.resolveElement(input);

            this.sortElement =
                this.resolveElement(
                    sortElement
                );

            this.loadMoreElement =
                this.resolveElement(
                    loadMoreElement
                );

            this.pageSize =
                this.normalizePositiveInteger(
                    pageSize,
                    20
                );

            this.modalSize =
                this.normalizePositiveInteger(
                    modalSize,
                    5
                );

            this.keywordSize =
                this.normalizePositiveInteger(
                    keywordSize,
                    8
                );

            this.minimumQueryLength =
                this.normalizePositiveInteger(
                    minimumQueryLength,
                    2
                );

            this.debounceDelay =
                this.normalizeNonNegativeInteger(
                    debounceDelay,
                    300
                );

            this.syncSortToUrl =
                Boolean(syncSortToUrl);

            this.sortUrlParam =
                this.normalizeString(sortUrlParam) ||
                "sort";

            this.productFields =
                Array.isArray(productFields)
                    ? [...productFields]
                    : [];

            this.modalProductFields =
                Array.isArray(
                    modalProductFields
                )
                    ? [...modalProductFields]
                    : [];

            this.keywordFields =
                Array.isArray(keywordFields)
                    ? [...keywordFields]
                    : [];

            this.onFacets =
                typeof onFacets === "function"
                    ? onFacets
                    : null;

            this.onKeywords =
                typeof onKeywords === "function"
                    ? onKeywords
                    : null;

            this.onResponse =
                typeof onResponse === "function"
                    ? onResponse
                    : null;

            this.onError =
                typeof onError === "function"
                    ? onError
                    : null;

            this.onSearchStart =
                typeof onSearchStart === "function"
                    ? onSearchStart
                    : null;

            this.onLoadingChange =
                typeof onLoadingChange ===
                    "function"
                    ? onLoadingChange
                    : null;

            this.debug = Boolean(debug);

            this.state = {
                page: {
                    query: "",
                    offset: 0,
                    total: null,
                    hasMore: true,
                    isLoading: false,
                    latestRequestId: 0,
                },

                modal: {
                    query: "",
                    offset: 0,
                    total: null,
                    hasMore: false,
                    isLoading: false,
                    latestRequestId: 0,
                },
            };

            this.debounceTimer = null;
            this.isInitialized = false;
            this.observer = null;

            this.previousFacetChangeHandler =
                this.facetManager?.onChange ||
                null;

            this.boundHandleInput =
                this.handleInput.bind(this);

            this.boundHandleSortChange =
                this.handleSortChange.bind(this);
        }

        /**
         * Escribe mensajes en consola cuando debug está activo.
         */
        log(...args) {
            if (!this.debug) return;

            console.info(
                "[NostoSearchController]",
                ...args
            );
        }

        /**
         * Convierte un valor en una cadena limpia.
         */
        normalizeString(value) {
            return String(value ?? "").trim();
        }

        /**
         * Convierte un valor en un entero positivo.
         */
        normalizePositiveInteger(
            value,
            fallback
        ) {
            const number = Number(value);

            return Number.isFinite(number) &&
                number > 0
                ? Math.floor(number)
                : fallback;
        }

        /**
         * Convierte un valor en un entero igual o mayor a cero.
         */
        normalizeNonNegativeInteger(
            value,
            fallback
        ) {
            const number = Number(value);

            return Number.isFinite(number) &&
                number >= 0
                ? Math.floor(number)
                : fallback;
        }

        /**
         * Resuelve un elemento desde un selector o nodo.
         */
        resolveElement(target) {
            if (target instanceof Element) {
                return target;
            }

            if (typeof target === "string") {
                return document.querySelector(
                    target
                );
            }

            return null;
        }

        /**
         * Normaliza el nombre de contexto recibido.
         */
        normalizeContext(
            context = "page"
        ) {
            return context === "modal"
                ? "modal"
                : "page";
        }

        /**
         * Obtiene el estado asociado a un contexto.
         */
        getContextState(context) {
            return this.state[
                this.normalizeContext(
                    context
                )
            ];
        }

        /**
         * Obtiene el renderer asociado a un contexto.
         */
        getRenderer(context) {
            return (
                this.normalizeContext(
                    context
                ) === "modal"
                    ? this.modalRenderer
                    : this.pageRenderer
            );
        }

        /**
         * Obtiene el HitStore asociado a un contexto.
         */
        getHitStore(context) {
            return (
                this.normalizeContext(
                    context
                ) === "modal"
                    ? this.modalHitStore
                    : this.pageHitStore
            );
        }

        /**
         * Valida las dependencias requeridas por Search.
         */
        validateDependencies() {
            const requiredDependencies = {
                client: this.client,
                facetManager:
                    this.facetManager,
                pageHitStore:
                    this.pageHitStore,
                modalHitStore:
                    this.modalHitStore,
                pageRenderer:
                    this.pageRenderer,
                modalRenderer:
                    this.modalRenderer,
                input: this.input,
            };

            for (const [
                name,
                dependency,
            ] of Object.entries(
                requiredDependencies
            )) {
                if (!dependency) {
                    throw new Error(
                        `NostoSearchController requiere la dependencia "${name}".`
                    );
                }
            }
        }

        /**
         * Inicializa Search y conecta sus eventos.
         */
        init() {
            if (this.isInitialized) {
                return this;
            }

            this.validateDependencies();
            this.bindEvents();
            this.bindFacetManager();
            this.bindInfiniteScroll();
            this.restoreSortFromUrl();

            this.isInitialized = true;

            return this;
        }

        /**
         * Desconecta eventos y cancela operaciones pendientes.
         */
        destroy() {
            this.input?.removeEventListener(
                "input",
                this.boundHandleInput
            );

            this.sortElement?.removeEventListener(
                "change",
                this.boundHandleSortChange
            );

            global.clearTimeout(
                this.debounceTimer
            );

            this.invalidateRequests(
                "page"
            );

            this.invalidateRequests(
                "modal"
            );

            this.facetManager.setChangeHandler(
                this.previousFacetChangeHandler
            );

            this.observer?.disconnect();
            this.observer = null;

            this.isInitialized = false;
        }

        /**
         * Conecta los eventos de input, sort y load more.
         */
        bindEvents() {
            this.input.addEventListener(
                "input",
                this.boundHandleInput
            );

            this.sortElement?.addEventListener(
                "change",
                this.boundHandleSortChange
            );
        }

        /**
 * Inicializa el observer utilizado para cargar más resultados.
 */
        bindInfiniteScroll() {
            if (
                !this.loadMoreElement ||
                typeof IntersectionObserver !==
                "function"
            ) {
                return;
            }

            this.observer?.disconnect();

            this.observer =
                new IntersectionObserver(
                    (entries) => {
                        const shouldLoad =
                            entries.some(
                                (entry) =>
                                    entry.isIntersecting
                            );

                        if (!shouldLoad) {
                            return;
                        }

                        this.loadMore();
                    },
                    {
                        root: null,
                        rootMargin:
                            "300px 0px",
                        threshold: 0,
                    }
                );

            this.observer.observe(
                this.loadMoreElement
            );
        }

        /**
         * Conecta el cambio de filtros con la recarga del SERP.
         */
        bindFacetManager() {
            this.facetManager.setChangeHandler(
                (detail) => {
                    if (
                        typeof this.previousFacetChangeHandler ===
                        "function"
                    ) {
                        this.previousFacetChangeHandler(
                            detail
                        );
                    }

                    this.handleFacetChange(
                        detail
                    );
                }
            );
        }

        /**
         * Devuelve el texto actual del input.
         */
        getInputQuery() {
            return this.normalizeString(
                this.input?.value
            );
        }

        /**
         * Comprueba si una búsqueda tiene la longitud mínima.
         */
        isValidQuery(query) {
            return (
                this.normalizeString(query)
                    .length >=
                this.minimumQueryLength
            );
        }

        /**
         * Define manualmente el valor del input.
         */
        setQueryValue(query) {
            const normalizedQuery =
                this.normalizeString(query);

            if (this.input) {
                this.input.value =
                    normalizedQuery;
            }

            return normalizedQuery;
        }

        /**
         * Devuelve el ordenamiento seleccionado para SERP.
         */
        getSort() {
            const value =
                this.sortElement?.value || "";

            const sortMap = {
                "price-descending": {
                    field: "price",
                    order: "desc",
                },
                "price-ascending": {
                    field: "price",
                    order: "asc",
                },
                "created-descending": {
                    field: "datePublished",
                    order: "desc",
                },
            };

            return sortMap[value] || null;
        }

        restoreSortFromUrl() {
            if (
                !this.syncSortToUrl ||
                !this.sortElement
            ) {
                return;
            }

            const url = new URL(
                global.location.href
            );

            const value =
                this.normalizeString(
                    url.searchParams.get(
                        this.sortUrlParam
                    )
                );

            if (!value) {
                return;
            }

            const hasOption =
                Array.from(
                    this.sortElement.options || []
                ).some(
                    (option) =>
                        option.value === value
                );

            if (hasOption) {
                this.sortElement.value = value;
            }
        }

        setSortInUrl() {
            if (!this.syncSortToUrl) {
                return;
            }

            const url = new URL(
                global.location.href
            );

            if (this.getSort()) {
                url.searchParams.set(
                    this.sortUrlParam,
                    this.sortElement.value
                );
            } else {
                url.searchParams.delete(
                    this.sortUrlParam
                );
            }

            global.history.replaceState(
                global.history.state || {},
                "",
                url
            );
        }

        /**
         * Construye la petición de la página de resultados.
         */
        buildPageRequest({
            query,
            from,
            size,
            includeFacets = true,
        }) {
            const sort = this.getSort();

            const filters =
                this.facetManager
                    .buildNostoFilters();

            return {
                query,

                products: {
                    fields: [
                        ...this.productFields,
                    ],

                    from,
                    size,

                    ...(includeFacets
                        ? { facets: ["*"] }
                        : {}),

                    ...(sort ? { sort } : {}),

                    ...(filters.length
                        ? { filter: filters }
                        : {}),
                },

                keywords: {
                    size: this.keywordSize,
                    fields: [
                        ...this.keywordFields,
                    ],
                },
            };
        }

        /**
         * Construye la petición del autocomplete.
         */
        buildModalRequest({
            query,
            size,
        }) {
            return {
                query,

                products: {
                    fields: [
                        ...this.modalProductFields,
                    ],

                    from: 0,
                    size,
                },

                keywords: {
                    size: this.keywordSize,
                    fields: [
                        ...this.keywordFields,
                    ],
                },
            };
        }

        /**
         * Construye las opciones de búsqueda según el contexto.
         */
        buildSearchOptions(
            context,
            {
                isKeyword = false,
            } = {}
        ) {
            const normalizedContext =
                this.normalizeContext(
                    context
                );

            return {
                redirect: false,

                track:
                    normalizedContext ===
                        "modal"
                        ? "autocomplete"
                        : "serp",

                ...(isKeyword
                    ? { isKeyword: true }
                    : {}),
            };
        }

        /**
         * Incrementa el ID de petición para invalidar respuestas anteriores.
         */
        invalidateRequests(context) {
            const state =
                this.getContextState(
                    context
                );

            state.latestRequestId += 1;

            return state.latestRequestId;
        }

        /**
         * Comprueba si una petición sigue siendo la más reciente.
         */
        isLatestRequest(
            context,
            requestId
        ) {
            return (
                this.getContextState(
                    context
                ).latestRequestId ===
                requestId
            );
        }

        /**
         * Actualiza el estado visual de carga de un contexto.
         */
        setLoading(
            context,
            isLoading
        ) {
            const normalizedContext =
                this.normalizeContext(
                    context
                );

            const state =
                this.getContextState(
                    normalizedContext
                );

            const renderer =
                this.getRenderer(
                    normalizedContext
                );

            state.isLoading =
                Boolean(isLoading);

            renderer.setLoading(
                state.isLoading
            );

            if (
                normalizedContext ===
                "page"
            ) {
                this.loadMoreElement?.classList.toggle(
                    "is-loading",
                    state.isLoading
                );

                this.loadMoreElement?.setAttribute(
                    "aria-busy",
                    String(state.isLoading)
                );
            }

            this.onLoadingChange?.(
                {
                    context:
                        normalizedContext,
                    isLoading:
                        state.isLoading,
                    controller: this,
                }
            );
        }

        /**
         * Ejecuta una búsqueda en el contexto indicado.
         */
        async search(
            context = "page",
            {
                query =
                this.getInputQuery(),
                mode = "replace",
                submit = false,
                isKeyword = false,
                includeFacets = true,
            } = {}
        ) {
            const normalizedContext =
                this.normalizeContext(
                    context
                );

            const normalizedQuery =
                this.normalizeString(query);

            const state =
                this.getContextState(
                    normalizedContext
                );

            const renderer =
                this.getRenderer(
                    normalizedContext
                );

            const hitStore =
                this.getHitStore(
                    normalizedContext
                );

            if (
                !this.isValidQuery(
                    normalizedQuery
                )
            ) {
                this.clearContext(
                    normalizedContext
                );

                return null;
            }

            if (
                state.isLoading &&
                normalizedContext === "page" &&
                mode === "append"
            ) {
                return null;
            }

            const isNewQuery =
                state.query !==
                normalizedQuery;

            if (isNewQuery) {
                mode = "replace";
                state.offset = 0;
                state.total = null;
                state.hasMore = true;
            }

            const from =
                normalizedContext === "page" &&
                    mode === "append"
                    ? state.offset
                    : 0;

            const size =
                normalizedContext === "modal"
                    ? this.modalSize
                    : this.pageSize;

            const requestId =
                ++state.latestRequestId;

            /*
             * Cada consulta que reemplaza el SERP limpia los
             * resultados anteriores para no mezclarlos con la
             * nueva búsqueda mientras llega la respuesta.
             */
            if (
                normalizedContext === "page" &&
                mode === "replace"
            ) {
                this.getRenderer(
                    normalizedContext
                ).clear();

                this.onSearchStart?.({
                    context:
                        normalizedContext,
                    mode,
                    query:
                        normalizedQuery,
                    isNewQuery,
                    controller: this,
                });
            }

            this.setLoading(
                normalizedContext,
                true
            );

            try {
                if (
                    submit &&
                    normalizedContext ===
                    "page" &&
                    isNewQuery
                ) {
                    await this.client
                        .recordSearchSubmit(
                            normalizedQuery
                        );
                }

                const request =
                    normalizedContext ===
                        "modal"
                        ? this.buildModalRequest({
                            query:
                                normalizedQuery,
                            size,
                        })
                        : this.buildPageRequest({
                            query:
                                normalizedQuery,
                            from,
                            size,
                            includeFacets,
                        });

                const response =
                    await this.client.search(
                        request,
                        this.buildSearchOptions(
                            normalizedContext,
                            {
                                isKeyword,
                            }
                        )
                    );

                if (
                    !this.isLatestRequest(
                        normalizedContext,
                        requestId
                    )
                ) {
                    return null;
                }

                const hits =
                    response.products.hits;

                if (mode === "replace") {
                    hitStore.replace(hits);
                } else {
                    hitStore.addMany(hits);
                }

                await renderer.render(
                    hits,
                    {
                        mode,
                        context: {
                            source:
                                normalizedContext ===
                                    "modal"
                                    ? "autocomplete"
                                    : "search",

                            track:
                                normalizedContext ===
                                    "modal"
                                    ? "autocomplete"
                                    : "serp",

                            query:
                                normalizedQuery,
                        },
                    }
                );

                if (
                    !this.isLatestRequest(
                        normalizedContext,
                        requestId
                    )
                ) {
                    return null;
                }

                state.query =
                    response.query ||
                    normalizedQuery;

                state.offset =
                    from + hits.length;

                state.total =
                    response.products.total;

                state.hasMore =
                    normalizedContext === "page" &&
                    hits.length === size &&
                    (
                        state.total <= 0 ||
                        state.offset <
                        state.total
                    );

                if (
                    normalizedContext ===
                    "page" &&
                    includeFacets &&
                    response.products.facets
                        .length
                ) {
                    this.onFacets?.(
                        response.products.facets,
                        {
                            response,
                            controller: this,
                            facetManager:
                                this.facetManager,
                        }
                    );
                }

                this.emitKeywords(
                    response.keywords.hits,
                    {
                        context:
                            normalizedContext,
                        query:
                            normalizedQuery,
                        response,
                    }
                );

                this.updateLoadMoreVisibility();

                this.onResponse?.(
                    response,
                    {
                        context:
                            normalizedContext,
                        mode,
                        controller: this,
                    }
                );

                return response;
            } catch (error) {
                if (
                    !this.isLatestRequest(
                        normalizedContext,
                        requestId
                    )
                ) {
                    return null;
                }

                if (
                    mode === "replace" ||
                    state.offset === 0
                ) {
                    renderer.renderError(
                        error
                    );
                } else {
                    renderer.renderAppendError(
                        error
                    );
                }

                if (
                    normalizedContext ===
                    "page"
                ) {
                    this.updateLoadMoreVisibility();
                }

                this.onError?.(
                    error,
                    {
                        context:
                            normalizedContext,
                        controller: this,
                    }
                );

                return null;
            } finally {
                if (
                    this.isLatestRequest(
                        normalizedContext,
                        requestId
                    )
                ) {
                    this.setLoading(
                        normalizedContext,
                        false
                    );
                }
            }
        }

        /**
 * Ejecuta una búsqueda explícita en la página de resultados.
 */
        async submitSearch(
            query =
                this.getInputQuery(),
            {
                isKeyword = false,
            } = {}
        ) {
            const normalizedQuery =
                this.setQueryValue(query);

            /*
             * Cancela el debounce y las respuestas pendientes del autocomplete
             * para evitar que sobrescriban las keywords de la búsqueda enviada.
             */
            global.clearTimeout(
                this.debounceTimer
            );

            this.clearContext(
                "modal"
            );

            this.facetManager.reset({
                emit: false,
                syncUrl: false,
            });

            return this.search(
                "page",
                {
                    query:
                        normalizedQuery,

                    mode:
                        "replace",

                    submit: true,
                    isKeyword,
                    includeFacets: true,
                }
            );
        }

        /**
         * Ejecuta una búsqueda de autocomplete.
         */
        async searchAutocomplete(
            query =
                this.getInputQuery()
        ) {
            return this.search(
                "modal",
                {
                    query,
                    mode: "replace",
                    submit: false,
                    includeFacets: false,
                }
            );
        }

        /**
         * Carga la siguiente página del SERP.
         */
        async loadMore() {
            const state =
                this.state.page;

            if (
                state.isLoading ||
                !state.hasMore ||
                !this.isValidQuery(
                    state.query
                )
            ) {
                return null;
            }

            return this.search(
                "page",
                {
                    query: state.query,
                    mode: "append",
                    submit: false,
                    includeFacets: false,
                }
            );
        }

        /**
         * Reacciona a la escritura del usuario y ejecuta autocomplete con debounce.
         */
        handleInput() {
            global.clearTimeout(
                this.debounceTimer
            );

            const query =
                this.getInputQuery();

            if (
                !this.isValidQuery(query)
            ) {
                this.clearContext(
                    "modal"
                );

                return;
            }

            this.debounceTimer =
                global.setTimeout(
                    () => {
                        this.searchAutocomplete(
                            query
                        );
                    },
                    this.debounceDelay
                );
        }

        /**
         * Reacciona a un cambio de ordenamiento del SERP.
         */
        async handleSortChange() {
            this.setSortInUrl();

            const query =
                this.state.page.query ||
                this.getInputQuery();

            if (
                !this.isValidQuery(query)
            ) {
                return null;
            }

            return this.search(
                "page",
                {
                    query,
                    mode: "replace",
                    includeFacets: true,
                }
            );
        }

        /**
 * Reacciona únicamente cuando los filtros deben aplicarse al SERP.
 */
        async handleFacetChange(
            detail = {}
        ) {
            const allowedTypes = [
                "apply",
                "reset",
            ];

            if (
                !allowedTypes.includes(
                    detail.type
                )
            ) {
                return null;
            }

            const query =
                this.state.page.query ||
                this.getInputQuery();

            if (
                !this.isValidQuery(query)
            ) {
                return null;
            }

            return this.search(
                "page",
                {
                    query,
                    mode: "replace",
                    includeFacets: true,
                }
            );
        }

        /**
         * Convierte los hits de keywords en una lista normalizada.
         */
        normalizeKeywordHits(
            keywordHits = []
        ) {
            if (
                !Array.isArray(keywordHits)
            ) {
                return [];
            }

            return keywordHits
                .map((hit) => {
                    if (
                        typeof hit === "string"
                    ) {
                        return hit.trim();
                    }

                    return this.normalizeString(
                        hit?.keyword
                    );
                })
                .filter(Boolean);
        }

        /**
         * Envía las keywords normalizadas al callback de UI.
         */
        emitKeywords(
            keywordHits,
            {
                context,
                query,
                response,
            }
        ) {
            if (!this.onKeywords) {
                return;
            }

            const normalizedQuery =
                this.normalizeString(query);

            const keywords =
                this.normalizeKeywordHits(
                    keywordHits
                ).filter(
                    (keyword) =>
                        keyword.toLowerCase() !==
                        normalizedQuery.toLowerCase()
                );

            this.onKeywords(
                keywords,
                {
                    context,
                    query:
                        normalizedQuery,
                    response,
                    controller: this,
                }
            );
        }

        /**
         * Limpia los resultados y el estado de un contexto.
         */
        clearContext(context) {
            const normalizedContext =
                this.normalizeContext(
                    context
                );

            const state =
                this.getContextState(
                    normalizedContext
                );

            this.invalidateRequests(
                normalizedContext
            );

            state.query = "";
            state.offset = 0;
            state.total = null;
            state.hasMore =
                normalizedContext ===
                "page";

            this.getHitStore(
                normalizedContext
            ).clear();

            this.getRenderer(
                normalizedContext
            ).clear();

            this.setLoading(
                normalizedContext,
                false
            );

            if (
                normalizedContext ===
                "page"
            ) {
                this.updateLoadMoreVisibility();
            }

            this.onKeywords?.(
                [],
                {
                    context:
                        normalizedContext,
                    query: "",
                    controller: this,
                }
            );
        }

        /**
         * Limpia ambos contextos y el input de búsqueda.
         */
        clear() {
            if (this.input) {
                this.input.value = "";
            }

            this.clearContext("page");
            this.clearContext("modal");

            this.facetManager.reset({
                emit: false,
                syncUrl: false,
            });
        }

        /**
         * Actualiza la visibilidad del botón de cargar más.
         */
        updateLoadMoreVisibility() {
            if (!this.loadMoreElement) {
                return;
            }

            const hasMore =
                this.state.page.hasMore;

            this.loadMoreElement.classList.toggle(
                "is-hidden",
                !hasMore
            );

            this.loadMoreElement.setAttribute(
                "aria-hidden",
                String(!hasMore)
            );
        }

        /**
 * Registra el clic de un producto según el contexto de búsqueda.
 */
        async recordProductClick(
            productId,
            context = "page",
            {
                fallbackHit = null,
            } = {}
        ) {
            const normalizedContext =
                this.normalizeContext(
                    context
                );

            const hit =
                this.getHitStore(
                    normalizedContext
                ).get(productId) ||
                fallbackHit;

            if (!hit) {
                this.log(
                    "No se encontró el hit para registrar el clic.",
                    {
                        productId,
                        context:
                            normalizedContext,
                    }
                );

                return false;
            }

            const track =
                normalizedContext === "modal"
                    ? "autocomplete"
                    : "serp";

            return this.client
                .recordSearchClick(
                    track,
                    hit
                );
        }

        /**
 * Registra el add to cart según el contexto de búsqueda.
 */
        async recordProductAddToCart(
            productId,
            context = "page",
            {
                fallbackHit = null,
            } = {}
        ) {
            const normalizedContext =
                this.normalizeContext(
                    context
                );

            const hit =
                this.getHitStore(
                    normalizedContext
                ).get(productId) ||
                fallbackHit;

            if (!hit) {
                this.log(
                    "No se encontró el hit para registrar el add to cart.",
                    {
                        productId,
                        context:
                            normalizedContext,
                    }
                );

                return false;
            }

            const track =
                normalizedContext === "modal"
                    ? "autocomplete"
                    : "serp";

            return this.client
                .recordSearchAddToCart(
                    track,
                    hit
                );
        }
    }

    global.NostoSearchController =
        NostoSearchController;
})(window);
